import React, { useState, useRef, DragEvent, ChangeEvent } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';

interface EventImageUploadProps {
  currentImageUrl?: string;
  onImageSelect: (file: File) => void;
  onImageRemove: () => void;
  loading?: boolean;
  error?: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];

export const EventImageUpload: React.FC<EventImageUploadProps> = ({
  currentImageUrl,
  onImageSelect,
  onImageRemove,
  loading = false,
  error,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Please select a valid image file (JPEG, PNG, or WebP)';
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return 'Image size must be less than 5MB';
    }

    return null;
  };

  const handleFile = (file: File) => {
    setValidationError(null);

    const error = validateFile(file);
    if (error) {
      setValidationError(error);
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Notify parent component
    onImageSelect(file);
  };

  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    setValidationError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onImageRemove();
  };

  const displayImageUrl = previewUrl || currentImageUrl;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Event Image
      </Typography>

      {(validationError || error) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setValidationError(null)}>
          {validationError || error}
          {error && error.includes('temporarily unavailable') && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              The system will automatically retry. Please wait...
            </Typography>
          )}
        </Alert>
      )}

      {displayImageUrl ? (
        <Paper
          elevation={2}
          sx={{
            p: 2,
            position: 'relative',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 400,
              mx: 'auto',
            }}
          >
            <Box
              component="img"
              src={displayImageUrl}
              alt="Event preview"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: 300,
                objectFit: 'contain',
                borderRadius: 1,
                display: 'block',
              }}
              onError={(e) => {
                // Handle broken image URLs
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={handleButtonClick}
              disabled={loading}
            >
              Replace Image
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleRemove}
              disabled={loading}
            >
              Remove Image
            </Button>
          </Box>
        </Paper>
      ) : (
        <Paper
          elevation={dragActive ? 4 : 1}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            border: dragActive ? '2px dashed' : '2px dashed',
            borderColor: dragActive ? 'primary.main' : 'grey.300',
            bgcolor: dragActive ? 'action.hover' : 'background.paper',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
          onClick={handleButtonClick}
        >
          {loading ? (
            <Box>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Uploading image...
              </Typography>
            </Box>
          ) : (
            <Box>
              <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" sx={{ mb: 1 }}>
                Drag and drop an image here, or click to select
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supported formats: JPEG, PNG, WebP (max 5MB)
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_EXTENSIONS.join(',')}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
    </Box>
  );
};
